import { isCharacterId } from '../game/characters';
import {
  BOARD_SIZE,
  validateSubmission,
  type LeaderboardEntry,
  type ScoreSubmission,
} from '../game/leaderboard';

/**
 * Talks to the global high score board.
 *
 * Every method answers `null` instead of throwing. A leaderboard that is slow,
 * unreachable, or having a bad day must never take the game down with it — the
 * caller falls back to the local cache and the player keeps playing.
 *
 * Spec: docs/global-leaderboard.md
 */

/**
 * The deployed Lambda Function URL.
 *
 * Baked in rather than read from an Amplify build variable: it is not a secret,
 * and a missing environment variable would silently ship a build with no board
 * at all. `VITE_LEADERBOARD_URL` still overrides it for local experiments.
 */
export const DEFAULT_LEADERBOARD_URL =
  'https://7n7o2v5e7x35lgzljq5v5vwdiq0vwgmy.lambda-url.us-east-1.on.aws/';

/** Long enough for a cold Lambda, short enough not to strand the results screen. */
const DEFAULT_TIMEOUT_MS = 5_000;

export class LeaderboardClient {
  constructor(
    private readonly baseUrl: string | undefined,
    // Wrapped, not `globalThis.fetch` directly. Stored bare and then called as
    // `this.fetchImpl(...)`, fetch receives the client as its receiver, and a
    // browser rejects that with "Illegal invocation". Node does not care, so
    // this failed only in the real thing — see the receiver test.
    private readonly fetchImpl: typeof fetch = (input, init) => globalThis.fetch(input, init),
    private readonly timeoutMs: number = DEFAULT_TIMEOUT_MS,
  ) {}

  /** True when there is somewhere to send scores. */
  get configured(): boolean {
    return typeof this.baseUrl === 'string' && this.baseUrl.length > 0;
  }

  async fetchBoard(limit: number = BOARD_SIZE): Promise<LeaderboardEntry[] | null> {
    if (!this.baseUrl) {
      return null;
    }
    const url = new URL(this.baseUrl);
    url.searchParams.set('limit', String(limit));
    return this.request(url.toString(), { method: 'GET' });
  }

  /**
   * Sends a finished run.
   *
   * The submission is checked against the same rule the server uses before it
   * leaves, so an impossible score costs no round trip and the player is not
   * left waiting on a rejection that was predictable here.
   */
  async submit(submission: ScoreSubmission): Promise<LeaderboardEntry[] | null> {
    if (!this.baseUrl || !validateSubmission(submission).ok) {
      return null;
    }
    return this.request(this.baseUrl, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(submission),
    });
  }

  private async request(url: string, init: RequestInit): Promise<LeaderboardEntry[] | null> {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), this.timeoutMs);
    try {
      const response = await this.fetchImpl(url, { ...init, signal: controller.signal });
      if (!response.ok) {
        return null;
      }
      return readEntries(await response.json());
    } catch {
      // Offline, aborted, CORS, malformed JSON — all the same to the player.
      return null;
    } finally {
      clearTimeout(timer);
    }
  }
}

/**
 * Builds the client the game actually uses.
 *
 * `VITE_LEADERBOARD_URL` wins whenever it is set *at all* — including when it
 * is set to the empty string, which switches the board off. The end-to-end
 * suite relies on that: it finishes real races, and without a way to turn
 * submission off every test run would write junk into a permanent board.
 */
export function createLeaderboardClient(): LeaderboardClient {
  const override = import.meta.env?.VITE_LEADERBOARD_URL;
  return new LeaderboardClient(
    typeof override === 'string' ? override : DEFAULT_LEADERBOARD_URL,
  );
}

function readEntries(payload: unknown): LeaderboardEntry[] | null {
  if (typeof payload !== 'object' || payload === null) {
    return null;
  }
  const entries = (payload as { entries?: unknown }).entries;
  if (!Array.isArray(entries)) {
    return null;
  }
  // A single unusable row should cost that row, not the whole board.
  return entries.filter(isEntry);
}

function isEntry(value: unknown): value is LeaderboardEntry {
  if (typeof value !== 'object' || value === null) {
    return false;
  }
  const row = value as Record<string, unknown>;
  return (
    typeof row['initials'] === 'string' &&
    typeof row['character'] === 'string' &&
    isCharacterId(row['character']) &&
    typeof row['score'] === 'number' &&
    Number.isFinite(row['score']) &&
    typeof row['survivedMs'] === 'number' &&
    typeof row['recordedAt'] === 'number'
  );
}
